import type { ShopperSearch } from '@/scapi';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import { IN_STOCK_ATTRIBUTE } from './in-stock';

type ProductSearchRefinement = ShopperSearch.schemas['ProductSearchRefinement'];

/** Category scoping — owned by the route, never shown or cleared by the filter UI. */
export const CATEGORY_ATTRIBUTE = 'cgid';

/** Single-select refinements: picking a value replaces the current one (SFRA radio behaviour). */
const EXCLUSIVE_ATTRIBUTES = new Set(['price']);

export const isExclusiveAttribute = (attributeId: string): boolean => EXCLUSIVE_ATTRIBUTES.has(attributeId);

/** SCAPI ORs multiple values of one attribute when they are pipe-joined in a single refine. */
const VALUE_SEPARATOR = '|';

export type RefineMap = Map<string, string[]>;

export interface AppliedFilter {
    attributeId: string;
    value: string;
    label: string;
}

export function parseRefines(refines: string[]): RefineMap {
    const map: RefineMap = new Map();
    for (const refine of refines) {
        const separatorIndex = refine.indexOf('=');
        if (separatorIndex <= 0) continue;
        const attributeId = refine.slice(0, separatorIndex);
        const values = refine
            .slice(separatorIndex + 1)
            .split(VALUE_SEPARATOR)
            .filter(Boolean);
        const existing = map.get(attributeId) ?? [];
        map.set(attributeId, [...existing, ...values.filter((value) => !existing.includes(value))]);
    }
    return map;
}

export function serializeRefines(map: RefineMap): string[] {
    return [...map.entries()]
        .filter(([, values]) => values.length > 0)
        .map(([attributeId, values]) => `${attributeId}=${values.join(VALUE_SEPARATOR)}`);
}

export function isRefinementSelected(refines: string[], attributeId: string, value: string): boolean {
    return parseRefines(refines).get(attributeId)?.includes(value) ?? false;
}

export function toggleRefinement(refines: string[], attributeId: string, value: string): string[] {
    const map = parseRefines(refines);
    const current = map.get(attributeId) ?? [];
    if (current.includes(value)) {
        map.set(
            attributeId,
            current.filter((v) => v !== value)
        );
    } else {
        map.set(attributeId, EXCLUSIVE_ATTRIBUTES.has(attributeId) ? [value] : [...current, value]);
    }
    return serializeRefines(map);
}

export function removeRefinement(refines: string[], attributeId: string, value: string): string[] {
    return isRefinementSelected(refines, attributeId, value) ? toggleRefinement(refines, attributeId, value) : refines;
}

export function setRefinement(refines: string[], attributeId: string, value: string | null): string[] {
    const map = parseRefines(refines);
    if (value === null) {
        map.delete(attributeId);
    } else {
        map.set(attributeId, [value]);
    }
    return serializeRefines(map);
}

export function clearRefinements(refines: string[]): string[] {
    const map = parseRefines(refines);
    const category = map.get(CATEGORY_ATTRIBUTE);
    return category ? serializeRefines(new Map([[CATEGORY_ATTRIBUTE, category]])) : [];
}

export function getFilterGroups(refinements: ProductSearchRefinement[] | undefined): ProductSearchRefinement[] {
    return (refinements ?? []).filter(
        (refinement) => refinement.attributeId !== CATEGORY_ATTRIBUTE && (refinement.values?.length ?? 0) > 0
    );
}

export function getAppliedFilters(
    refines: string[],
    refinements: ProductSearchRefinement[] | undefined,
    inStockLabel: string
): AppliedFilter[] {
    const applied: AppliedFilter[] = [];
    for (const [attributeId, values] of parseRefines(refines)) {
        if (attributeId === CATEGORY_ATTRIBUTE) continue;
        for (const value of values) {
            const label =
                attributeId === IN_STOCK_ATTRIBUTE
                    ? inStockLabel
                    : (refinements?.find((r) => r.attributeId === attributeId)?.values?.find((v) => v.value === value)
                          ?.label ?? value);
            applied.push({ attributeId, value, label });
        }
    }
    return applied;
}

export function buildRefinedSearch(search: string, refines: string[]): string {
    const params = new URLSearchParams(search);
    params.delete(PRODUCT_SEARCH_QUERY_PARAMS.REFINE);
    refines.forEach((refine) => params.append(PRODUCT_SEARCH_QUERY_PARAMS.REFINE, refine));
    params.delete('page');
    params.set(PRODUCT_SEARCH_QUERY_PARAMS.OFFSET, '0');
    return `?${params.toString()}`;
}

export function buildSortedSearch(search: string, sort: string): string {
    const params = new URLSearchParams(search);
    params.set(PRODUCT_SEARCH_QUERY_PARAMS.SORT, sort);
    params.delete('page');
    params.set(PRODUCT_SEARCH_QUERY_PARAMS.OFFSET, '0');
    return `?${params.toString()}`;
}
